import type { Icon } from "@phosphor-icons/react";
import { FileText } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCollections } from "~/api/queries";
import type { Document } from "~/api/types";
import { DocumentRow } from "~/features/documents/DocumentRow";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";

export interface DocumentListProps {
  documents: Document[] | undefined;
  isLoading: boolean;
  /** Off inside a collection's own page, where every row would repeat the heading. */
  showCollection?: boolean;
  emptyTitle?: string;
  emptyMessage: string;
  emptyIcon?: Icon;
  emptyAction?: ReactNode;
}

/**
 * Every document list in the app — home, drafts, archive, trash, search, collection.
 * Rows are borderless hover pills, platform-style; the slight negative inline margin lets
 * the hover background outgrow the text column while titles stay aligned with the heading.
 */
export function DocumentList({
  documents,
  isLoading,
  showCollection = true,
  emptyTitle,
  emptyMessage,
  emptyIcon = FileText,
  emptyAction,
}: DocumentListProps) {
  const { t } = useTranslation();
  const { data: collections } = useCollections();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("Loading")} />
      </div>
    );
  }

  if (!documents?.length) {
    return (
      <Empty
        icon={emptyIcon}
        {...(emptyTitle ? { title: emptyTitle } : {})}
        message={emptyMessage}
        {...(emptyAction ? { action: emptyAction } : {})}
      />
    );
  }

  const collectionOf = (collectionId: string | null) =>
    showCollection && collectionId
      ? collections?.find((collection) => collection.id === collectionId)
      : undefined;

  return (
    <ul className="-mx-3 flex flex-col gap-0.5">
      {documents.map((document) => (
        <DocumentRow
          key={document.id}
          document={document}
          collection={collectionOf(document.collectionId)}
        />
      ))}
    </ul>
  );
}
